import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorBanner, AuthRequiredBanner, SkeletonCard } from '@/components/ui';
import { useControlStatus, useOnboardingState, isAuthError, getErrorMessage } from '@/lib/queries';

export const Route = createFileRoute('/org/status/')({
  component: OrgStatusPage,
});

function OrgStatusPage() {
  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useControlStatus();
  const {
    data: onboardingData,
    isLoading: onboardingLoading,
    error: onboardingError,
    refetch: refetchOnboarding,
  } = useOnboardingState();

  const isLoading = statusLoading || onboardingLoading;
  const error = statusError || onboardingError;

  // Set document title
  useEffect(() => {
    document.title = 'System Status - 11to1';
  }, []);

  const refetch = () => {
    refetchStatus();
    refetchOnboarding();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="System Status" />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="System Status" />
        {isAuthError(error) ? (
          <AuthRequiredBanner />
        ) : (
          <ErrorBanner message={getErrorMessage(error)} onRetry={refetch} />
        )}
      </div>
    );
  }

  if (!statusData || !onboardingData) {
    return (
      <div className="space-y-6">
        <PageHeader title="System Status" />
        <div className="card">
          <p className="text-muted">Unable to load system status.</p>
        </div>
      </div>
    );
  }

  const overallHealth = getOverallHealth(statusData, onboardingData);

  return (
    <div className="space-y-6">
      <PageHeader title="System Status" />

      {/* Overall Status */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text">Overall System Health</h2>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${getHealthStatusClass(overallHealth)}`}
          >
            {overallHealth.label}
          </div>
        </div>
        <p className="text-muted mb-4">{overallHealth.description}</p>

        {overallHealth.status === 'warn' && (
          <div className="flex gap-2">
            <button className="btn-primary" disabled>
              Complete Clinic Setup (Coming Soon)
            </button>
            <button className="btn-secondary" disabled>
              Complete Staffing Setup (Coming Soon)
            </button>
          </div>
        )}
      </div>

      {/* Secrets Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text mb-4">Configuration Status</h3>
        <div className="space-y-3">
          {Object.entries(statusData.secrets).map(([key, secretData]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-text capitalize">
                {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
              </span>
              <div
                className={`px-2 py-1 rounded text-xs font-medium ${
                  secretData.hasValue ? 'bg-success/20 text-success' : 'bg-error/20 text-error'
                }`}
              >
                {secretData.hasValue ? 'Configured' : 'Missing'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Onboarding Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text mb-4">Setup Progress</h3>

        {/* Clinic */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-text">Clinic Setup</span>
            <span className="text-sm text-muted">
              {getCompletionPercentage(onboardingData.clinic)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${getCompletionPercentage(onboardingData.clinic)}%` }}
            />
          </div>
          {!isSectionComplete(onboardingData.clinic) && (
            <button className="text-primary text-sm mt-1 inline-block" disabled>
              Continue setup → (Coming Soon)
            </button>
          )}
        </div>

        {/* Staffing */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-text">Staffing Setup</span>
            <span className="text-sm text-muted">
              {getCompletionPercentage(onboardingData.staffing)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${getCompletionPercentage(onboardingData.staffing)}%` }}
            />
          </div>
          {!isSectionComplete(onboardingData.staffing) && (
            <button className="text-primary text-sm mt-1 inline-block" disabled>
              Continue setup → (Coming Soon)
            </button>
          )}
        </div>
      </div>

      {/* Control Plane Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text mb-4">System Services</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-text">Control Plane</p>
            <p className="text-sm text-muted">Administrative services and APIs</p>
          </div>
          <div
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusData.control.outboxOk && statusData.control.auditOk
                ? 'bg-success/20 text-success'
                : 'bg-error/20 text-error'
            }`}
          >
            {statusData.control.outboxOk && statusData.control.auditOk
              ? 'Healthy'
              : 'Issues Detected'}
          </div>
        </div>

        {(!statusData.control.outboxOk || !statusData.control.auditOk) && (
          <div className="mt-3 p-3 bg-error/10 border border-error/20 rounded">
            <p className="text-sm text-error">Control plane services are experiencing issues.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function getOverallHealth(
  statusData: { control: { outboxOk: boolean; auditOk: boolean } },
  onboardingData: { clinic: Record<string, boolean>; staffing: Record<string, boolean> }
) {
  const clinicComplete = isSectionComplete(onboardingData.clinic);
  const staffingComplete = isSectionComplete(onboardingData.staffing);
  const systemHealthy = statusData.control.outboxOk && statusData.control.auditOk;

  if (clinicComplete && staffingComplete && systemHealthy) {
    return {
      status: 'good',
      label: 'All Systems Operational',
      description: 'Your organization is fully set up and ready to handle appointments.',
    };
  } else if (systemHealthy) {
    return {
      status: 'warn',
      label: 'Setup In Progress',
      description: 'System is healthy but onboarding is not complete.',
    };
  } else {
    return {
      status: 'error',
      label: 'Issues Detected',
      description: 'System issues detected. Please review configuration and try again.',
    };
  }
}

function getHealthStatusClass(health: { status: string }) {
  switch (health.status) {
    case 'good':
      return 'bg-success/20 text-success';
    case 'warn':
      return 'bg-warning/20 text-warning';
    case 'error':
      return 'bg-error/20 text-error';
    default:
      return 'bg-muted/20 text-muted';
  }
}

/** The backend returns per-step booleans only — a section is complete when every step is done. */
function isSectionComplete(onboardingSection: Record<string, boolean>): boolean {
  return Object.values(onboardingSection).every(Boolean);
}

function getCompletionPercentage(onboardingSection: Record<string, boolean>): number {
  const fields = Object.keys(onboardingSection);
  const completed = fields.filter((key) => onboardingSection[key]).length;
  return Math.round((completed / fields.length) * 100);
}
